import React, {useEffect, useRef, useState} from "react";
import { Input, Spin } from "antd";
import {db} from "../../../../core/helpers/database"
import { ref, push, update } from "firebase/database";
import {useSelector} from "react-redux";
import {RootState} from "../../../../core/redux/store";
import {utils} from "../../../../core/helpers";

interface Message {
    owner: string;
    message: string;
    isHidden?: boolean;
}

interface Props {
    messages: Message[];
    input: string;
    setInput: (val: string) => void;
    loading: boolean;
    onSend: () => void;
    topicName: string;
    levelName: string | null;
}

const ChatInterface: React.FC<Props> = ({
                                            messages,
                                            input,
                                            setInput,
                                            loading,
                                            onSend,
                                            topicName,
                                            levelName
                                        }) => {

    const displayMessages = messages.filter(m => !m.isHidden);

    const { user } = useSelector((state: RootState) => state.user);

    const [conversationId, setConversationId] = useState(null);

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    useEffect(() => {
        setConversationId(null)
    }, [levelName, topicName]);

    useEffect(() => {
        if (!levelName || !topicName) return;
        if (displayMessages.length === 1 && conversationId === null){
            const d = new Date();
            const d_formatted = `${d.getFullYear()}-${d.getMonth().toString().padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}_${d.getHours().toString().padStart(2, "0")}-${d.getMinutes().toString().padStart(2, "0")}-${d.getSeconds().toString().padStart(2, "0")}`;

            // @ts-ignore
            const chatsRef = ref(db, `alm/${user.email.replaceAll(".", "~")}/${levelName}/${topicName}`)
            const newEntry = push(chatsRef, {chat_type: "text", datetime: d_formatted, totalTokens: 0})
            // @ts-ignore
            setConversationId(newEntry.key)
        }
        if (conversationId){
            // @ts-ignore
            const reference = ref(db, `alm/${user.email.replaceAll(".", "~")}/${levelName}/${topicName}/${conversationId}`)
            update(reference, {
                chat_log: displayMessages.map(x => ({
                    content: x.message,
                    role: x.owner === "You" ? "user":"ai",
                    tokens: 0,
                    responseTime: 0
                }))
            })
        }
    }, [messages]);

    return (
        <div className="d-flex flex-column w-100" style={{ height: '650px', backgroundColor: '#f8f9fa' }}>

            <div className="p-3 d-flex align-items-center bg-white border-bottom" style={{ zIndex: 50 }}>
                <div className="d-flex align-items-start flex-grow-1 mx-2">
                    <div>
                        <h5 className="mb-0 text-dark fw-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {topicName}
                        </h5>
                    </div>
                </div>
            </div>

            <div className="flex-grow-1 overflow-auto px-4 pt-4" style={{ paddingBottom: '20px' }}>
                {displayMessages.length === 0 && !loading ? (
                    <div className="d-flex justify-content-center align-items-center h-50 text-muted">
                        <div className="text-center">
                            <i className="isax isax-message-text fs-1 mb-2 opacity-25"></i>
                            <p>Start conversation...</p>
                        </div>
                    </div>
                ) : (
                    displayMessages.map((msg, idx) => (
                        <div
                            key={idx}
                            className={`d-flex mb-3 w-100 ${msg.owner === 'You' ? 'justify-content-end' : 'justify-content-start'}`}
                        >
                            <div
                                className={`p-3 shadow-sm`}
                                style={{
                                    maxWidth: '80%',
                                    // User: Blue, AI: White
                                    backgroundColor: msg.owner === 'You' ? '#3b82f6' : '#ffffff',
                                    borderTopLeftRadius: '1rem',
                                    borderTopRightRadius: '1rem',
                                    borderBottomRightRadius: msg.owner === 'You' ? '0' : '1rem',
                                    borderBottomLeftRadius: msg.owner === 'AI' ? '0' : '1rem'
                                }}
                            >
                                <p className={`mb-0 ${
                                    msg.owner === 'You'
                                        ? 'text-white' // User text color
                                        : 'text-dark'  // AI text color
                                }`} style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                            </div>
                        </div>
                    ))
                )}

                {loading && (
                    <div className="d-flex justify-content-start mb-3">
                        <div
                            className="p-3 rounded-4 text-dark bg-white shadow-sm"
                            style={{ borderBottomLeftRadius: 0 }}
                        >
                            <Spin size="small" />
                        </div>
                    </div>
                )}
            </div>

            <div className="p-3 w-100 bg-white border-top">
                <div
                    className="d-flex align-items-center p-2 rounded-4"
                    style={{ backgroundColor: '#f1f5f9' }}
                >
                    <Input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Type your message..."
                        className="flex-grow-1 border-0 bg-transparent text-dark shadow-none"
                        style={{
                            color: '#334155',
                            backgroundColor: 'transparent',
                            fontSize: '15px'
                        }}
                        disabled={loading}
                    />
                    <button
                        onClick={onSend}
                        className="btn btn-primary rounded-circle p-2 d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                        disabled={!input.trim() || loading}
                    >
                        <i className="isax isax-send-1 text-white fs-5"></i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatInterface;